"use client";
import { useEffect, useState } from "react";
import TextArea from "@/components/views/add-place/TextArea";
import ChatBubbleOvalLeftIcon from "@heroicons/react/24/solid/ChatBubbleOvalLeftIcon";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import toast from "react-hot-toast";
import { createHomepageCommentFormAction } from "@/actions/homepage/create-comment";
import { homepageCommentSchema } from "@/actions/homepage/schema";
import { StarIcon } from "@heroicons/react/24/outline";
import { useSite } from "@/lib/hooks/site";
import ContestDrawer, { ContestFormData } from "@/components/ui/ContestDrawer";
import { useTranslation } from "@/hooks/translationContext";
import { useSession } from "next-auth/react";
import { registerFormAction } from "@/actions/auth";
import { updateCommentContestData } from "@/actions/comment/update-contest";
import { POST_REGISTER_SESSION_NAME } from "@/@core/constants/auth";

export default function HomepageCommentForm() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedStar, setSelectedStar] = useState(5);
    const [showContestDrawer, setShowContestDrawer] = useState(false);
    const [pendingCommentData, setPendingCommentData] = useState<any>(null);
    const site = useSite();
    const isCampaignActive = (site?.setting as any)?.campaignIsActive == 1;
    const { t } = useTranslation();
    const { status: sessionStatus } = useSession();

    const form = useForm<z.infer<ReturnType<typeof homepageCommentSchema>>>({
        resolver: zodResolver(homepageCommentSchema(t)),
        defaultValues: {
            fullName: "",
            comment: "",
            rating: 5,
        },
    });

    const handleSuccessful = (formData: any) => {
        toast.success(t("listpage-comment-pending-approval-info"));

        form.reset({ fullName: "", comment: "", rating: 5 });
        setSelectedStar(5);
    };

    const submitComment = async (data: any, contestData?: ContestFormData) => {
        const submitData = {
            ...data,
            rating: selectedStar,
            ...(contestData && {
                phone: contestData.phone,
                instagram_username: contestData.instagram,
                address: contestData.address,
                email: contestData.email,
            }),
        };

        const response = await createHomepageCommentFormAction(submitData);
        if (response?.success) {
            handleSuccessful(data);
            setIsSubmitting(false);
            return response.commentId || true;
        } else {
            toast.error(
                response?.error ||
                t("listpage-comment-generic-error"),
            );
            setIsSubmitting(false);
            return false;
        }
    };

    const handleSubmit = async (data: any) => {
        setIsSubmitting(true);
        const result = await submitComment(data);
        if (result && isCampaignActive) {
            setPendingCommentData({ ...data, commentId: typeof result === 'number' ? result : null });
            setShowContestDrawer(true);
        }
    };

    useEffect(() => {
        const scrollToForm = () => {
            const element = document.getElementById("homepage-comment-form");
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        };

        let observer: ResizeObserver | undefined;
        if (window.location.hash === "#yorum-ekle") {
            setTimeout(scrollToForm, 300);
            // Re-scroll when lazy-loaded content changes page height
            let lastHeight = document.body.scrollHeight;
            observer = new ResizeObserver(() => {
                const newHeight = document.body.scrollHeight;
                if (newHeight !== lastHeight) {
                    lastHeight = newHeight;
                    scrollToForm();
                }
            });
            observer.observe(document.body);
            setTimeout(() => observer?.disconnect(), 10000);
        }

        const onHashChange = () => {
            if (window.location.hash === "#yorum-ekle") {
                setTimeout(scrollToForm, 300);
            }
        };
        window.addEventListener("hashchange", onHashChange);

        return () => {
            window.removeEventListener("hashchange", onHashChange);
            observer?.disconnect();
        };
    }, []);

    return (
        <div>
            <Form {...form}>
                <form
                    id="homepage-comment-form"
                    onSubmit={form.handleSubmit((data) => handleSubmit(data))}
                    className="flex flex-col h-full max-w-xl"
                >
                    <div className="flex flex-col gap-4">
                        <FormField
                            control={form.control}
                            name="fullName"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <TextArea
                                            rows={1}
                                            icon={<ChatBubbleOvalLeftIcon className="size-5" />}
                                            placeholder={t("venue-detail-comment-fullname-placeholder")}
                                            id={field.name}
                                            name="fullName"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="comment"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <TextArea
                                            rows={10}
                                            className="!h-24"
                                            icon={<ChatBubbleOvalLeftIcon className="size-5" />}
                                            placeholder={t("homepage-comment-form-placeholder", { defaultValue: "Yorumunuzu/Sorunuzu Yazın" })}
                                            id={field.name}
                                            name="comment"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="flex items-center gap-4">
                            <span className="font-semibold text-assistant">Puanınız:</span>
                            <div className="flex gap-2 items-center">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        onClick={() => setSelectedStar(star)}
                                        aria-label={t("comment-form-rating-star-label", {
                                          defaultValue: "{{star}} yıldız ver",
                                          star,
                                        })}
                                        className="focus:outline-none"
                                    >
                                        <StarIcon
                                            className={`w-6 h-6 max-md:w-8 max-md:h-8 ${
                                                star <= selectedStar ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                                            } transition-colors`}
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className={cn(
                                "flex bg-[#20b038] border border-[#20b038] rounded-[16px] text-white text-[14px] font-semibold tracking-[-0.14px] hover:bg-[#1a9930] transition-colors",
                                isSubmitting && "opacity-70 cursor-not-allowed"
                            )}
                            style={{ fontFamily: "'DM Sans', sans-serif" }}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? t("blog-detail-comment-submit-loading") : t("blog-detail-comment-submit-button")}
                        </Button>
                    </div>
                </form>
            </Form>

            <ContestDrawer
                isOpen={showContestDrawer}
                onClose={() => {
                    setShowContestDrawer(false);
                    setIsSubmitting(false);
                    setPendingCommentData(null);
                }}
                onSubmit={async (contestData: ContestFormData) => {
                    setIsSubmitting(true);

                    if (!pendingCommentData) {
                        setIsSubmitting(false);
                        return;
                    }

                    const pending = pendingCommentData;

                    try {
                        // Step 1: Save contest data — does not require an account,
                        // so it must never be blocked by registration failures
                        const contestResult = pending.commentId
                            ? await updateCommentContestData(
                                pending.commentId,
                                { phone: contestData.phone, instagram_username: contestData.instagram, address: contestData.address, email: contestData.email },
                                "page"
                              )
                            : { success: !!(await submitComment(pending, contestData)) };

                        if (!contestResult.success) {
                            toast.error((contestResult as any).error || t("contest-save-error"));
                            setIsSubmitting(false);
                            return;
                        }

                        setShowContestDrawer(false);
                        setPendingCommentData(null);
                        toast.success(t("contest-save-success"));

                        // Step 2: Create an account / sign in, best-effort
                        if (sessionStatus === "unauthenticated") {
                            const registerResult = await registerFormAction({
                                nameAndSurname: pending.fullName,
                                email: contestData.email,
                                password: contestData.password,
                                termsOfUse: contestData.termsOfUse,
                            });

                            if (registerResult?.success) {
                                sessionStorage.setItem(POST_REGISTER_SESSION_NAME, "1");
                                toast.success(t("user-register-guest-account-success"));
                                setTimeout(() => window.location.reload(), 1000);
                            } else {
                                toast.error(
                                    registerResult?.error ||
                                    t("user-register-error")
                                );
                            }
                        }
                    } catch (error) {
                        toast.error(t("contest-generic-error"));
                    } finally {
                        setIsSubmitting(false);
                    }
                }}
                title={t("contest-form-title")}
                description={t("venue-detail-reply-form-description")}
            />
        </div>
    );
}
